<template>
  <TopGradientBlockLayout>
    <template v-slot:top>
      <PasswordGuard/>
    </template>
    <div class="text-xl font-bold text-text leading-tight mb-11">{{ $t('title') }}</div>
    <div class="relative">
      <PasswordGuardOverlay/>
      <div class="space-y-12">
        <div class="grid lg:grid-cols-2 items-center col-gap-12 row-gap-6">
          <BaseInput v-model.trim="username"
                     :label="$t('loginLabel')"
                     :disabled="$store.state.cabinet.passwordGuard.active"
                     @focusin="clearError('username')"
                     :class="{'--error':!!serverErrors.username}">
            <template slot="errors">
              <div class="error" v-if="!!serverErrors.username">
                {{ serverErrors.username }}
              </div>
            </template>
          </BaseInput>
          <div class="text-xs text-text-ternary">{{ $t('loginTip') }}</div>
        </div>
        <div class="grid lg:grid-cols-2 items-center col-gap-12 row-gap-6">
          <BaseInput v-model.trim="$v.email.$model"
                     :label="$t('mailLabel')"
                     :disabled="$store.state.cabinet.passwordGuard.active "
                     @focusin="clearError('email')"
                     :class="{'--error': $v.email.$error || !!serverErrors.email}">
            <template slot="errors">
              <div class="error" v-if="!$v.email.required">{{ $t('errors.required') }}</div>
              <div class="error" v-if="!$v.email.email">{{ $t('incorrectField') }}</div>
              <div class="error" v-if="$v.email.required && !!serverErrors.email">
                {{ serverErrors.email }}
              </div>
            </template>
          </BaseInput>
          <div class="text-xs text-text-ternary">{{ $t('mailTip') }}</div>
        </div>
        <div class="grid lg:grid-cols-2 items-center col-gap-12 row-gap-6">
          <PasswordInput v-model.trim="password"
                         :password-reveal="true"
                         :label="$t('passwordLabel')"
                         :disabled="$store.state.cabinet.passwordGuard.active"
                         :error="$v.password.$error">
            <template slot="errors">
              <div class="error" v-if="!!password && !$v.password.required">{{ $t('errors.required') }}</div>
            </template>
          </PasswordInput>
        </div>
        <div class="grid lg:grid-cols-2 items-center col-gap-12 row-gap-6" v-if="!!password">
          <PasswordInput v-model.trim="$v.repeat_password.$model"
                         :password-reveal="true"
                         :label="$t('repeatPasswordLabel')"
                         :disabled="$store.state.cabinet.passwordGuard.active"
                         :error="$v.repeat_password.$error">
            <template slot="errors">
              <div class="error" v-if="!$v.repeat_password.required">{{ $t('errors.required') }}</div>
              <div class="error" v-if="$v.repeat_password.required && !$v.repeat_password.sameAsPassword">
                {{ $t('differentPasswords') }}
              </div>
            </template>
          </PasswordInput>
        </div>
        <div class="grid lg:grid-cols-2 items-center col-gap-12 row-gap-6">
          <PasswordInput v-model.trim="pin"
                         :label="$t('PINLabel')"
                         :password-reveal="true"
                         name="PIN"
                         :disabled="$store.state.cabinet.passwordGuard.active"
                         :error="$v.pin.$error || $v.pin.$invalid">
            <template slot="errors">
              <div class="error" v-if="!!pin && !$v.pin.required">{{ $t('errors.required') }}</div>
              <div class="error" v-if="!!pin && !$v.pin.numeric">{{ $t('errors.numeric') }}</div>
              <div class="error" v-if="$v.pin.numeric && (!$v.pin.minLength || !$v.pin.maxLength)">
                {{ $t('errors.PINLength') }}
              </div>
            </template>
          </PasswordInput>
          <div class="text-xs text-text-ternary">{{ $t('PINTip') }}</div>
        </div>
        <div class="grid lg:grid-cols-2 items-center col-gap-12 row-gap-6" v-if="!!pin">
          <PasswordInput v-model.trim="$v.repeat_pin.$model"
                         :label="$t('repeatPINLabel')"
                         :password-reveal="true"
                         name="PIN"
                         :disabled="$store.state.cabinet.passwordGuard.active"
                         :error="$v.repeat_pin.$error">
            <template slot="errors">
              <div class="error" v-if="!$v.repeat_pin.required">{{ $t('errors.required') }}</div>
              <div class="error" v-if="$v.repeat_pin.required && !$v.repeat_pin.sameAsPin">
                {{ $t('differentPINS') }}
              </div>
            </template>
          </PasswordInput>
        </div>
        <BaseButton theme="gray"
                    class="w-91"
                    :loading="saving"
                    v-show="!guarded"
                    @click="saveSettings">
          {{ $t('save') }}
        </BaseButton>
        <UserConnectedAccounts :auth_accounts="user_info.data.auth_accounts"/>
      </div>
    </div>
  </TopGradientBlockLayout>
</template>

<script>
import {required, email, minLength, maxLength, numeric, sameAs, requiredIf} from 'vuelidate/lib/validators';
import {mapActions, mapMutations, mapState} from "vuex";
import TopGradientBlockLayout from "@/components/layouts/cabinet/TopGradientBlockLayout";
import PasswordGuard from "@/components/common/PasswordGuard/PasswordGuard";
import BaseButton from "@/components/UI/BaseButton";
import BaseInput from "@/components/UI/BaseInput/BaseInput";
import PasswordGuardOverlay from "@/components/common/PasswordGuard/PasswordGuardOverlay";
import PasswordInput from "@/components/UI/BaseInput/PasswordInput";
import UserConnectedAccounts from "@/components/cabinet/ProfileSettings/UserConnectedAccounts/UserConnectedAccounts";

export default {
  name: "TheProfileSettings",
  components: {
    UserConnectedAccounts,
    PasswordInput,
    PasswordGuardOverlay,
    BaseInput,
    BaseButton,
    PasswordGuard,
    TopGradientBlockLayout,
  },
  computed: {
    ...mapState('cabinet/user', ["user_info"]),
    ...mapState('cabinet/passwordGuard', {
      guarded: (state) => state.active
    })
  },
  data() {
    return {
      saving: false,
      username: '',
      email: '',
      password: '',
      repeat_password: '',
      pin: '',
      repeat_pin: '',
      serverErrors: {
        username: null,
        email: null,
        password: null,
      }
    }
  },
  validations: {
    email: {
      required,
      email
    },
    password: {
      required: requiredIf(function () {
        return !!this.password
      }),
      minLength: minLength(6)
    },
    repeat_password: {
      required: requiredIf(function () {
        return !!this.password
      }),
      sameAsPassword: sameAs('password')
    },
    pin: {
      numeric,
      required: requiredIf(function () {
        return !!this.pin
      }),
      minLength: minLength(4),
      maxLength: maxLength(4)
    },
    repeat_pin: {
      numeric,
      required: requiredIf(function () {
        return !!this.pin
      }),
      minLength: minLength(4),
      maxLength: maxLength(4),
      sameAsPin: sameAs('pin')
    }
  },
  methods: {
    ...mapActions('cabinet/user', ['updateUser']),
    ...mapMutations('cabinet/user', ['setUserInfo']),
    ...mapActions('cabinet/passwordGuard', ['checkUserIsUnlocked']),
    async saveSettings() {
      try {
        const isUnlocked = await this.checkUserIsUnlocked()
        this.$v.$touch();
        if (!this.$v.$invalid && isUnlocked) {
          this.saving = true;
          let data = {
            id: this.user_info.data.id,
            username: this.username,
            email: this.email
          };
          if (!!this.password) {
            data.password = this.password;
          }
          if (!!this.pin) {
            data.pin = this.pin;
          }
          const res = await this.updateUser(data);
          if (res.body.success) {
            this.$toasted.success('Изменения сохранены');
            this.setUserInfo(res.body.data.user)
            this.$v.$reset();
            if (!!this.password) {
              this.password = null;
              this.repeat_password = null;
            }
            if (!!this.pin) {
              this.pin = null;
              this.repeat_pin = null;
            }
          } else {
            this.$toasted.error('Произошла ошибка');
            this.showErrors(res.body.message);
          }
        }
      } catch (err) {
        console.log(err)
        this.$toasted.error('Произошла ошибка');
      } finally {
        this.saving = false;
      }
    },
    showErrors(mssgs) {
      !!mssgs.username ? this.serverErrors.username = mssgs.username : '';
      !!mssgs.email ? this.serverErrors.email = mssgs.email : '';
    },
    clearErrors() {
      this.serverErrors = {username: '', email: ''}
    },
    clearError(name) {
      this.serverErrors[name] = null
    },
    setFields() {
      let user = this.user_info.data;
      this.username = user.username;
      this.email = user.email;
    },
    clearFields() {
      this.username = null;
      this.email = null;
      this.password = null;
      this.repeat_password = null;
      this.pin = null;
      this.repeat_pin = null;
      this.$v.$reset();
    }
  },
  watch: {
    guarded: {
      immediate: true,
      handler(val) {
        if (!val) {
          this.setFields();
        } else {
          this.clearFields();
        }
      }
    }
  },
  i18n: {
    messages: {
      en: {
        title: 'Profile settings',
        loginLabel: 'Login (for authorization)',
        loginTip: 'Used for authorization in the cabinet',
        mailLabel: 'Email',
        mailTip: 'Needed to recover your password',
        passwordLabel: 'New password',
        repeatPasswordLabel: 'Repeat new password',
        differentPasswords: 'Different passwords',
        PINLabel: 'New PIN',
        PINTip: 'Uses to unlock profile settings on site',
        repeatPINLabel: 'Repeat new PIN',
        differentPINS: 'Different PINs',
        incorrectField: 'Incorrect',
        save: 'Save'
      },
      ru: {
        title: 'Настройки профиля',
        loginLabel: 'Логин (для авторизации)',
        loginTip: 'Используется для авторизации в кабинете',
        mailLabel: 'Электронная почта',
        mailTip: 'Нужна для восстановления пароля',
        passwordLabel: 'Новый пароль',
        repeatPasswordLabel: 'Повторите новый пароль',
        differentPasswords: 'Пароли не совпадают',
        PINLabel: 'Новый PIN',
        PINTip: 'Используется для доступа к закрытым настройкам на сайте',
        repeatPINLabel: 'Повторите PIN',
        differentPINS: 'PIN коды не совпадают',
        incorrectField: 'Поле заполнено некорректно',
        save: 'Сохранить'
      }
    }
  }
}
</script>